import {useRef, useState} from 'react';
import {AnyQuestion, LegalQuestion, QuestionType} from "crqlar-react-common/dist/models/forms/form";
import {Media} from "crqlar-react-common/dist/models/common/media.model";
import {SignaturePadRef} from "crqlar-react-common/dist/components/SignaturePad";
import {CloudinaryService} from "crqlar-react-common/dist/services/cloudinary.service";

interface UseFormSignaturesReturn {
    signaturePadRefs: React.MutableRefObject<Map<string, SignaturePadRef>>;
    signatureEmptyState: Map<string, boolean>;
    uploadingSignatures: Set<string>;
    validationErrors: Set<string>;
    setSignatureEmptyState: React.Dispatch<React.SetStateAction<Map<string, boolean>>>;
    setValidationErrors: React.Dispatch<React.SetStateAction<Set<string>>>;
    uploadSignatures: (
        questions: AnyQuestion[],
        formAnswers: Map<string, string | string[] | boolean | number | Media | Media[]>
    ) => Promise<{
        success: boolean;
        mergedAnswers: Map<string, string | string[] | boolean | number | Media | Media[]>;
    }>;
    isSignatureQuestionValid: (question: LegalQuestion, questionId: string) => boolean;
}

export default function useFormSignatures(): UseFormSignaturesReturn {
    const signaturePadRefs = useRef<Map<string, SignaturePadRef>>(new Map());
    const [signatureEmptyState, setSignatureEmptyState] = useState<Map<string, boolean>>(new Map());
    const [uploadingSignatures, setUploadingSignatures] = useState<Set<string>>(new Set());
    const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());

    const uploadSignatures = async (
        questions: AnyQuestion[],
        formAnswers: Map<string, string | string[] | boolean | number | Media | Media[]>
    ): Promise<{
        success: boolean;
        mergedAnswers: Map<string, string | string[] | boolean | number | Media | Media[]>;
    }> => {
        // Clear previous validation errors
        setValidationErrors(new Set());

        // Find all LEGAL questions with signature requirement
        const signatureQuestions = questions.filter(q =>
            q.type === QuestionType.LEGAL &&
            (q as LegalQuestion).requireSignature
        );

        const uploadErrors = new Set<string>();
        const uploadedSignatures = new Map<string, Media>();

        for (const question of signatureQuestions) {
            const ref = signaturePadRefs.current.get(question.id);
            if (ref) {
                // Check if signature is empty
                if (ref.isEmpty() && question.required) {
                    uploadErrors.add(question.id);
                    continue;
                }

                // Upload signature if not empty
                if (!ref.isEmpty()) {
                    try {
                        // Mark as uploading
                        setUploadingSignatures(prev => new Set(prev).add(question.id));

                        // Get signature file
                        const file = await ref.getSignatureFile();
                        if (!file) {
                            if (question.required) {
                                uploadErrors.add(question.id);
                            }
                            continue;
                        }

                        // Upload the file
                        const media = await CloudinaryService.upload(file);

                        if (media) {
                            // Store uploaded signature locally (don't update state yet)
                            uploadedSignatures.set(question.id, {
                                mediaURI: media.secure_url,
                                publicId: media.public_id,
                                provider: 'cloudinary'
                            });
                        } else if (question.required) {
                            uploadErrors.add(question.id);
                        }
                    } catch (error) {
                        console.error('Error uploading signature:', error);
                        if (question.required) {
                            uploadErrors.add(question.id);
                        }
                    } finally {
                        // Remove from uploading set
                        setUploadingSignatures(prev => {
                            const newSet = new Set(prev);
                            newSet.delete(question.id);
                            return newSet;
                        });
                    }
                }
            } else if (question.required) {
                // Required signature question but no ref found
                uploadErrors.add(question.id);
            }
        }

        // If there are upload errors, show them and return failure
        if (uploadErrors.size > 0) {
            setValidationErrors(uploadErrors);
            return {
                success: false,
                mergedAnswers: formAnswers
            };
        }

        // Merge uploaded signatures with existing form answers
        const mergedAnswers = new Map(formAnswers);
        uploadedSignatures.forEach((media, questionId) => {
            mergedAnswers.set(questionId, media);
        });

        return {
            success: true,
            mergedAnswers
        };
    };

    const isSignatureQuestionValid = (question: LegalQuestion, questionId: string): boolean => {
        if (!question.required) {
            return true;
        }

        if (question.requireSignature) {
            // Check if signature is empty (or not yet drawn)
            const isEmpty = signatureEmptyState.get(questionId);
            // If isEmpty is undefined, assume it's empty (not yet drawn)
            // If isEmpty is true, signature is empty
            return isEmpty === false;
        }

        return false;
    };

    return {
        signaturePadRefs,
        signatureEmptyState,
        uploadingSignatures,
        validationErrors,
        setSignatureEmptyState,
        setValidationErrors,
        uploadSignatures,
        isSignatureQuestionValid
    };
}
